import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAnalytics from '../hooks/useAnalytics';
import useScrollPosition from '../hooks/useScrollPosition';
import { LiveIcon, GitHubIcon, ArrowLeftIcon } from './Icons';

const ProjectDetail = ({ project, onClose }) => {
    const navigate = useNavigate();
    const { trackProjectView, trackClick } = useAnalytics();
    const { restoreScrollPosition } = useScrollPosition();

    useEffect(() => {
        if (project) {
            trackProjectView(`${project.title}_detail`, project.id);
        }
    }, [project, trackProjectView]);

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    const handleLinkClick = (linkType, url) => {
        trackClick(`project_detail_${linkType}`, {
            project_title: project.title,
            project_id: project.id,
            url: url
        });
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            // Restore scroll position when navigating back to homepage
            restoreScrollPosition('homepage', { delay: 100 });
            navigate('/');
        }
    };

    if (!project) {
        return (
            <div className="min-h-screen bg-cream-100 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="font-serif text-2xl font-medium text-charcoal-900 mb-4">Project not found</h1>
                    <button
                        onClick={handleClose}
                        className="btn-primary"
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream-100">
            {/* Header with back button */}
            <div className="sticky top-0 bg-cream-100/95 backdrop-blur-md border-b border-border-light z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={handleClose}
                        className="flex items-center space-x-2 text-charcoal-600 hover:text-charcoal-900 transition-colors"
                    >
                        <ArrowLeftIcon className="w-5 h-5" />
                        <span className="hidden sm:inline">Back to Projects</span>
                        <span className="sm:hidden">Back</span>
                    </button>
                    <div className="flex items-center space-x-3">
                        {project.year && (
                            <span className="text-charcoal-500 text-sm">{project.year}</span>
                        )}
                        {project.featured && (
                            <span className="bg-coral-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                                Featured
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                {/* Hero Section */}
                <div className="mb-8 sm:mb-12">
                    <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-charcoal-900 mb-4">{project.title}</h1>
                    <p className="text-lg md:text-xl text-charcoal-600 mb-8 leading-relaxed">{project.description}</p>

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-3 sm:gap-4">
                        {project.github && (
                            <a
                                href={project.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('github', project.github)}
                                className="btn-secondary"
                            >
                                <GitHubIcon className="w-5 h-5" />
                                <span>View Code</span>
                            </a>
                        )}
                        {project.live && !project.playstore && !project.appstore && (
                            <a
                                href={project.live}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('live', project.live)}
                                className="btn-primary"
                            >
                                <LiveIcon className="w-5 h-5" />
                                <span>Live Demo</span>
                            </a>
                        )}
                        {project.playstore && (
                            <a
                                href={project.playstore}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('playstore', project.playstore)}
                                className="inline-block hover:opacity-80 transition-opacity"
                            >
                                <img
                                    src="/images/projects/download-playstore.svg"
                                    alt="Get it on Google Play"
                                    className="h-[44px] w-[148px] object-contain"
                                />
                            </a>
                        )}
                        {project.appstore && (
                            <a
                                href={project.appstore}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('appstore', project.appstore)}
                                className="inline-block hover:opacity-80 transition-opacity"
                            >
                                <img
                                    src="/images/projects/download-appstore.svg"
                                    alt="Download on the App Store"
                                    className="h-[44px] w-[148px] object-contain"
                                />
                            </a>
                        )}
                    </div>
                </div>

                {/* Project Image */}
                <div className="mb-8 sm:mb-12">
                    <div className="bg-white rounded-2xl overflow-hidden border border-border-light shadow-soft">
                        <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-auto object-contain"
                        />
                    </div>
                </div>

                {/* Project Details Grid */}
                <div className="grid md:grid-cols-3 gap-8 md:gap-12 mb-8 sm:mb-12">
                    {/* Technologies Used */}
                    <div className="md:col-span-2">
                        <h2 className="font-serif text-xl md:text-2xl font-medium text-charcoal-900 mb-4">Technologies Used</h2>
                        <div className="flex flex-wrap gap-2">
                            {project.technologies.map((tech, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-2 bg-cream-200 text-sm rounded-full text-charcoal-700 border border-border-light hover:bg-coral-50 hover:text-coral-600 hover:border-coral-200 transition-colors"
                                >
                                    {tech}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Project Info */}
                    <div>
                        <h2 className="font-serif text-xl md:text-2xl font-medium text-charcoal-900 mb-4">Project Info</h2>
                        <div className="space-y-4">
                            {project.year && (
                                <div>
                                    <span className="text-charcoal-500 text-sm">Year</span>
                                    <p className="text-charcoal-900 font-medium">{project.year}</p>
                                </div>
                            )}
                            <div>
                                <span className="text-charcoal-500 text-sm">Status</span>
                                <p className="text-charcoal-900 font-medium capitalize">{project.status || 'Completed'}</p>
                            </div>
                            {project.featured && (
                                <div>
                                    <span className="text-charcoal-500 text-sm">Recognition</span>
                                    <p className="text-coral-600 font-medium">Featured Project</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Long Description */}
                {project.longDescription && (
                    <div className="mb-8 sm:mb-12">
                        <h2 className="font-serif text-xl md:text-2xl font-medium text-charcoal-900 mb-4">About This Project</h2>
                        <div className="prose prose-lg max-w-none">
                            <p className="text-charcoal-600 text-base md:text-lg leading-relaxed whitespace-pre-line">
                                {project.longDescription}
                            </p>
                        </div>
                    </div>
                )}

                {/* Project Links Section */}
                <div className="bg-white rounded-2xl p-6 sm:p-8 border border-border-light shadow-soft">
                    <h2 className="font-serif text-xl md:text-2xl font-medium text-charcoal-900 mb-6">Project Links</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {project.github && (
                            <a
                                href={project.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('github', project.github)}
                                className="flex items-center space-x-3 p-4 bg-cream-100 hover:bg-cream-200 rounded-xl transition-colors border border-border-light"
                            >
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-charcoal-900 text-white">
                                    <GitHubIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-charcoal-900">Source Code</h3>
                                    <p className="text-sm text-charcoal-500">View on GitHub</p>
                                </div>
                            </a>
                        )}
                        {project.live && !project.playstore && !project.appstore && (
                            <a
                                href={project.live}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('live', project.live)}
                                className="flex items-center space-x-3 p-4 bg-cream-100 hover:bg-cream-200 rounded-xl transition-colors border border-border-light"
                            >
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-coral-500 text-white">
                                    <LiveIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-charcoal-900">Live Demo</h3>
                                    <p className="text-sm text-charcoal-500">Try it out</p>
                                </div>
                            </a>
                        )}
                        {project.playstore && (
                            <a
                                href={project.playstore}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('playstore', project.playstore)}
                                className="flex items-center space-x-3 p-4 bg-cream-100 hover:bg-cream-200 rounded-xl transition-colors border border-border-light"
                            >
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cream-200">
                                    <img src="/images/projects/google-play.svg" alt="" className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-charcoal-900">Google Play Store</h3>
                                    <p className="text-sm text-charcoal-500">Download for Android</p>
                                </div>
                            </a>
                        )}
                        {project.appstore && (
                            <a
                                href={project.appstore}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleLinkClick('appstore', project.appstore)}
                                className="flex items-center space-x-3 p-4 bg-cream-100 hover:bg-cream-200 rounded-xl transition-colors border border-border-light"
                            >
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cream-200">
                                    <img src="/images/projects/apple-store-app.svg" alt="" className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-charcoal-900">Apple App Store</h3>
                                    <p className="text-sm text-charcoal-500">Download for iOS</p>
                                </div>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;
